import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_ORDER = ['Green', 'Yellow', 'Orange', 'Red', 'Blue', 'Purple'];
const STATUS_VAR = {
  Green: 'green', Yellow: 'yellow', Orange: 'orange',
  Red: 'red', Blue: 'blue', Purple: 'purple',
};

function parseDMY(str) {
  if (!str) return null;
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(str).trim());
  if (!m) return null;
  return new Date(+m[3], +m[2] - 1, +m[1]);
}
function fmtDMY(str) {
  const d = parseDMY(str);
  if (!d) return str || '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtISO(str) {
  if (!str) return '—';
  const d = new Date(str + 'T00:00:00');
  if (isNaN(d.getTime())) return str;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

// Builds one row per company/day where colour, sample count or
// certified-since date changed — mirrors the standalone dashboard's logic.
// A drop into "not sampled" (blank) is deliberately not logged as a change.
function buildChangeLog(companies) {
  const rows = [];
  companies.forEach((c) => {
    for (let i = 1; i < c.history.length; i++) {
      const prev = c.history[i - 1], cur = c.history[i];
      const prevColour = prev.colour || null, curColour = cur.colour || null;
      const prevSamples = prev.samples ?? null, curSamples = cur.samples ?? null;
      const prevSince = prev.since || null, curSince = cur.since || null;

      const colourChanged = prevColour !== curColour;
      const samplesChanged = curSamples !== null && prevSamples !== curSamples;
      const sinceChanged = curSince !== null && prevSince !== curSince;
      let entryExit = null;
      if (!prevColour && curColour) entryExit = 'Entered list';
      else if (prevColour && !curColour) entryExit = 'Exited list';

      if (colourChanged || samplesChanged || sinceChanged || entryExit) {
        rows.push({
          date: cur.date, name: c.name, regNo: c.regNo,
          prevColour, curColour, colourChanged,
          samplesChange: samplesChanged ? `${prevSamples ?? '—'} → ${curSamples ?? '—'}` : null,
          sinceChange: sinceChanged ? `${fmtDMY(prevSince)} → ${fmtDMY(curSince)}` : null,
          entryExit,
        });
      }
    }
  });
  rows.sort((a, b) => a.date.localeCompare(b.date) || a.name.localeCompare(b.name));
  return rows;
}

const RussiaRegisterPage = () => {
  const { user } = useAuth();
  const isAdmin = user && user.role === 'admin';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [activeStatuses, setActiveStatuses] = useState(new Set());
  const [changedOnly, setChangedOnly] = useState(false);
  const [samplesOnly, setSamplesOnly] = useState(false);
  const [sortKey, setSortKey] = useState('no');
  const [sortDir, setSortDir] = useState(1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr('');
      try {
        const r = await fetch(`${API}/api/russia-register/data`, { cache: 'no-store' });
        if (r.status === 404) { if (!cancelled) setData(null); return; }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const companies = data?.companies || [];

  const changeLog = useMemo(() => buildChangeLog(companies), [companies]);

  const stateOptions = useMemo(() => Object.keys(data?.stateCounts || {}), [data]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = companies.filter((c) => {
      if (activeStatuses.size && !activeStatuses.has(c.currentColour)) return false;
      if (stateFilter && c.state !== stateFilter) return false;
      if (changedOnly && !c.changed) return false;
      if (samplesOnly && !c.samplesFlagged) return false;
      if (q) {
        const hay = `${c.name} ${c.regNo} ${c.state}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    rows = [...rows].sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === 'since') { av = parseDMY(a.since) || new Date(0); bv = parseDMY(b.since) || new Date(0); }
      if (sortKey === 'currentSamples') { av = av == null ? -1 : av; bv = bv == null ? -1 : bv; }
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return -1 * sortDir;
      if (av > bv) return 1 * sortDir;
      return 0;
    });
    return rows;
  }, [companies, search, stateFilter, activeStatuses, changedOnly, samplesOnly, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => -d);
    else { setSortKey(key); setSortDir(1); }
  };

  const toggleStatus = (s) => {
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
  };

  if (loading) return <div className="rr-root"><style>{RR_CSS}</style><div className="rr-loading">Loading register…</div></div>;
  if (err) return <div className="rr-root"><style>{RR_CSS}</style><div className="rr-loading rr-error">Failed to load: {err}</div></div>;

  if (!data) {
    return (
      <div className="rr-root" data-testid="russia-register-page">
        <style>{RR_CSS}</style>
        <div className="rr-wrap">
          <div className="rr-empty-state">
            <h2>Russian Federation Website Data</h2>
            <p>No register has been uploaded yet.</p>
            {isAdmin ? (
              <>
                <Link to="/admin/data" className="rr-upload-btn">Upload it from Admin Tools → Data Ops</Link>
                <p className="rr-hint" style={{ marginTop: 14 }}>
                  Open the "Russian Federation Website Data" tab there, pick the Enterprises workbook, review the preview, then publish.
                </p>
              </>
            ) : (
              <p className="rr-hint">Ask an admin to upload the Enterprises workbook from Admin Tools → Data Ops.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const maxStatus = Math.max(1, ...STATUS_ORDER.filter((s) => data.colourCounts[s]).map((s) => data.colourCounts[s]));
  const stateEntries = Object.entries(data.stateCounts || {});
  const maxState = Math.max(1, ...stateEntries.map(([, v]) => v));

  return (
    <div className="rr-root" data-testid="russia-register-page">
      <style>{RR_CSS}</style>
      <div className="rr-wrap">
        <header className="rr-top">
          <div>
            <span className="rr-eyebrow">Fish &amp; Crustacean Export · India → Russia</span>
            <h1>Russian Federation Website Data</h1>
            <p className="rr-sub">
              Daily compliance status of Indian processing units certified to export shelled &amp;
              deshelled crustaceans — live, fresh, chilled, frozen, steamed or boiled — to the Russian Federation.
            </p>
          </div>
          <div className="rr-meta">
            <div>Snapshot range</div>
            <div className="rr-meta-val">{fmtISO(data.dates[0])} – {fmtISO(data.dates[data.dates.length - 1])}</div>
            <div style={{ marginTop: 6 }}>Enterprises tracked</div>
            <div className="rr-meta-val">{data.totalCompanies}</div>
            {isAdmin && (
              <Link to="/admin/data" className="rr-upload-btn" style={{ marginTop: 12, display: 'inline-block' }}>
                ↑ Upload updated register
              </Link>
            )}
          </div>
        </header>

        <section className="rr-panels">
          <div className="rr-panel">
            <h3>Status distribution</h3>
            <div className="rr-panel-sub">Current colour classification across all enterprises</div>
            {STATUS_ORDER.filter((s) => data.colourCounts[s]).sort((a, b) => data.colourCounts[b] - data.colourCounts[a]).map((s) => (
              <div className="rr-bar-row" key={s}>
                <div className="rr-bar-name"><span className="rr-dot" style={{ background: `var(--rr-${STATUS_VAR[s]})` }} />{s}</div>
                <div className="rr-bar-track"><div className="rr-bar-fill" style={{ width: `${100 * data.colourCounts[s] / maxStatus}%`, background: `var(--rr-${STATUS_VAR[s]})` }} /></div>
                <div className="rr-bar-val">{data.colourCounts[s]}</div>
              </div>
            ))}
          </div>
          <div className="rr-panel">
            <h3>By state</h3>
            <div className="rr-panel-sub">Where certified processing units are located</div>
            {stateEntries.map(([name, count]) => (
              <div className="rr-bar-row" key={name}>
                <div className="rr-bar-name">{name}</div>
                <div className="rr-bar-track"><div className="rr-bar-fill" style={{ width: `${100 * count / maxState}%`, background: 'var(--rr-accent)' }} /></div>
                <div className="rr-bar-val">{count}</div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="rr-sec-head">
            <h2>Change log</h2>
            <span className="rr-hint">
              {changeLog.length
                ? `${changeLog.length} change${changeLog.length === 1 ? '' : 's'} across ${data.dates.length} snapshots — sorted by date`
                : `No changes detected across ${data.dates.length} snapshots`}
            </span>
          </div>
          <div className="rr-table-wrap">
            <div className="rr-table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Date</th><th>Company</th><th>Reg. No.</th><th>Colour change</th>
                    <th>Sample count change</th><th>Certified-since change</th><th>Entry / exit</th>
                  </tr>
                </thead>
                <tbody>
                  {changeLog.length === 0 ? (
                    <tr className="rr-empty-row"><td colSpan={7}>No colour, sample count or certified-since changes were recorded in this window.</td></tr>
                  ) : changeLog.map((r, i) => (
                    <tr key={i}>
                      <td className="rr-mono">{fmtISO(r.date)}</td>
                      <td className="rr-co-name">{r.name}</td>
                      <td className="rr-mono">{r.regNo}</td>
                      <td>
                        {r.colourChanged ? (
                          <span className="rr-colour-change">
                            <span className="rr-dot" style={{ background: r.prevColour ? `var(--rr-${STATUS_VAR[r.prevColour]})` : 'var(--rr-line)' }} />{r.prevColour || '—'}
                            <span className="rr-arrow">→</span>
                            <span className="rr-dot" style={{ background: r.curColour ? `var(--rr-${STATUS_VAR[r.curColour]})` : 'var(--rr-line)' }} />{r.curColour || '—'}
                          </span>
                        ) : <span className="rr-muted">—</span>}
                      </td>
                      <td className="rr-mono">{r.samplesChange || <span className="rr-muted">—</span>}</td>
                      <td className="rr-mono">{r.sinceChange || <span className="rr-muted">—</span>}</td>
                      <td>{r.entryExit ? <span className="rr-badge">{r.entryExit}</span> : <span className="rr-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section>
          <div className="rr-sec-head">
            <h2>Full register</h2>
            <span className="rr-hint">Click a column heading to sort · click a status chip below to filter</span>
          </div>

          <div className="rr-legend">
            {STATUS_ORDER.filter((s) => data.colourCounts[s]).map((s) => (
              <span key={s} className={`rr-chip${activeStatuses.has(s) ? ' rr-chip-active' : ''}`} onClick={() => toggleStatus(s)}>
                <span className="rr-dot" style={{ background: `var(--rr-${STATUS_VAR[s]})` }} />{s} <span className="rr-mono rr-chip-count">{data.colourCounts[s]}</span>
              </span>
            ))}
            {activeStatuses.size > 0 && <span className="rr-chip rr-chip-clear" onClick={() => setActiveStatuses(new Set())}>Clear</span>}
          </div>

          <div className="rr-filterbar">
            <div className="rr-search">
              <input type="text" placeholder="Search company, reg. no. or state…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
              <option value="">All states</option>
              {stateOptions.map((s) => <option key={s} value={s}>{s} ({data.stateCounts[s]})</option>)}
            </select>
            <span className={`rr-chip${changedOnly ? ' rr-chip-active' : ''}`} onClick={() => setChangedOnly((v) => !v)}>
              <span className="rr-dot" style={{ background: 'var(--rr-accent)' }} /> Changed only
            </span>
            <span className={`rr-chip${samplesOnly ? ' rr-chip-active' : ''}`} onClick={() => setSamplesOnly((v) => !v)}>
              <span className="rr-dot" style={{ background: 'var(--rr-accent)' }} /> Samples &gt; 0
            </span>
            <span className="rr-filter-count">{filteredRows.length} of {companies.length} shown</span>
          </div>

          <div className="rr-table-wrap">
            <div className="rr-table-scroll">
              <table>
                <thead>
                  <tr>
                    <th className="num" onClick={() => toggleSort('no')}>#{sortKey === 'no' && (sortDir > 0 ? ' ▲' : ' ▼')}</th>
                    <th onClick={() => toggleSort('name')}>Company{sortKey === 'name' && (sortDir > 0 ? ' ▲' : ' ▼')}</th>
                    <th onClick={() => toggleSort('state')}>State{sortKey === 'state' && (sortDir > 0 ? ' ▲' : ' ▼')}</th>
                    <th onClick={() => toggleSort('currentColour')}>Status{sortKey === 'currentColour' && (sortDir > 0 ? ' ▲' : ' ▼')}</th>
                    <th onClick={() => toggleSort('since')}>Since{sortKey === 'since' && (sortDir > 0 ? ' ▲' : ' ▼')}</th>
                    <th className="num" onClick={() => toggleSort('currentSamples')}>Samples{sortKey === 'currentSamples' && (sortDir > 0 ? ' ▲' : ' ▼')}</th>
                    <th>7-day trend</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr className="rr-empty-row"><td colSpan={7}>No enterprises match these filters.</td></tr>
                  ) : filteredRows.map((c) => (
                    <tr key={`${c.regNo}-${c.no}`}>
                      <td className="num rr-mono">{c.no}</td>
                      <td>
                        <div className="rr-co-name">{c.name}{c.changed && <span className="rr-badge">CHANGED</span>}</div>
                        <div className="rr-co-sub">{c.regNo} · {c.state}</div>
                      </td>
                      <td>{c.state}</td>
                      <td>
                        <span className="rr-status-chip" style={{ background: `var(--rr-${STATUS_VAR[c.currentColour]}-bg)`, color: `var(--rr-${STATUS_VAR[c.currentColour]})` }}>
                          <span className="rr-dot" style={{ background: `var(--rr-${STATUS_VAR[c.currentColour]})` }} />{c.currentColour || '—'}
                        </span>
                      </td>
                      <td className="rr-mono">{fmtDMY(c.since)}</td>
                      <td className="num">{c.currentSamples ?? '—'}</td>
                      <td>
                        <div className="rr-trend">
                          {c.history.map((h, i) => (
                            <span key={i} className="rr-trend-dot" title={`${fmtISO(h.date)}: ${h.colour || '—'}${h.samples != null ? `, samples ${h.samples}` : ''}`}
                              style={{ background: h.colour ? `var(--rr-${STATUS_VAR[h.colour]})` : 'var(--rr-line)' }} />
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <footer className="rr-footer">
          <span>
            Source: <span className="rr-mono">{data.generatedFrom || 'Enterprises workbook'}</span>
            {data.category ? ` · category: ${data.category}` : ''}. Status colours and dates are reproduced exactly
            as recorded in the source register — consult the issuing authority for their official definitions.
          </span>
          <span>{data.uploadedAt ? `Last uploaded: ${new Date(data.uploadedAt).toLocaleString()}` : ''}</span>
        </footer>
      </div>
    </div>
  );
};

// Self-contained, scoped CSS (fixed light theme) — same pattern as
// GeneralStoresPage's DASHBOARD_CSS, so this page doesn't depend on or
// leak into the app's global dark/light theme variables.
const RR_CSS = `
.rr-root {
  --rr-paper: #ffffff;
  --rr-surface: #ffffff;
  --rr-surface-2: #f1f3ee;
  --rr-ink: #16211d;
  --rr-ink-soft: #42504a;
  --rr-muted: #6d7a73;
  --rr-line: #dcdfd6;
  --rr-accent: #0d6e6b;

  --rr-green: #178a3f;   --rr-green-bg: #dff5e4;
  --rr-yellow: #b8860b;  --rr-yellow-bg: #fbedc6;
  --rr-orange: #d2510f;  --rr-orange-bg: #fbe1cf;
  --rr-red: #d21f2f;     --rr-red-bg: #fbdadd;
  --rr-blue: #1450d6;    --rr-blue-bg: #dbe6fd;
  --rr-purple: #8112c9;  --rr-purple-bg: #ecd9fa;

  font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
  background: var(--rr-paper);
  color: var(--rr-ink);
  min-height: 100vh;
}
.rr-root * { box-sizing: border-box; }
.rr-loading { padding: 40px; color: var(--rr-muted); }
.rr-loading.rr-error { color: var(--rr-red); }
.rr-wrap { max-width: 1220px; margin: 0 auto; padding: 28px 24px 60px; }

.rr-empty-state { text-align: center; padding: 80px 20px; color: var(--rr-ink-soft); }
.rr-empty-state h2 { margin: 0 0 8px; }
.rr-hint { color: var(--rr-muted); font-size: 13px; }

.rr-top { display: flex; flex-wrap: wrap; gap: 16px 32px; justify-content: space-between; align-items: flex-start; padding-bottom: 22px; margin-bottom: 22px; border-bottom: 1px solid var(--rr-line); }
.rr-eyebrow { font-size: 11.5px; letter-spacing: .09em; text-transform: uppercase; color: var(--rr-accent); font-weight: 700; display: block; margin-bottom: 8px; }
.rr-top h1 { font-size: 30px; font-weight: 800; letter-spacing: -.01em; margin: 0; }
.rr-sub { color: var(--rr-ink-soft); font-size: 14.5px; max-width: 60ch; margin: 8px 0 0; line-height: 1.5; }
.rr-meta { text-align: right; font-size: 13px; color: var(--rr-muted); min-width: 200px; }
.rr-meta-val { color: var(--rr-ink); font-weight: 700; font-variant-numeric: tabular-nums; }

.rr-upload-btn { font-size: 12.5px; font-weight: 650; background: var(--rr-accent); color: #fff; border: none; border-radius: 6px; padding: 8px 14px; cursor: pointer; font-family: inherit; text-decoration: none; }
.rr-upload-btn:hover { opacity: .92; }

.rr-panels { display: grid; grid-template-columns: 1.1fr 1fr; gap: 16px; margin-bottom: 34px; }
.rr-panel { background: var(--rr-surface); border: 1px solid var(--rr-line); border-radius: 14px; padding: 18px 20px 14px; }
.rr-panel h3 { font-size: 14px; font-weight: 700; margin: 0 0 2px; }
.rr-panel-sub { font-size: 12px; color: var(--rr-muted); margin-bottom: 14px; }
.rr-bar-row { display: grid; grid-template-columns: 120px 1fr 44px; align-items: center; gap: 10px; margin-bottom: 9px; }
.rr-bar-name { font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 7px; }
.rr-dot { width: 9px; height: 9px; border-radius: 50%; flex: none; display: inline-block; }
.rr-bar-track { height: 16px; background: var(--rr-surface-2); border-radius: 5px; overflow: hidden; }
.rr-bar-fill { height: 100%; border-radius: 5px; min-width: 3px; }
.rr-bar-val { font-size: 12.5px; font-weight: 700; text-align: right; font-variant-numeric: tabular-nums; }

.rr-sec-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
.rr-sec-head h2 { font-size: 18px; font-weight: 700; margin: 0; }
.rr-hint { font-size: 12.5px; color: var(--rr-muted); }

.rr-legend { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
.rr-chip { display: inline-flex; align-items: center; gap: 6px; border: 1px solid var(--rr-line); background: var(--rr-surface); border-radius: 999px; padding: 5px 11px 5px 8px; font-size: 12.5px; font-weight: 600; cursor: pointer; user-select: none; color: var(--rr-ink-soft); }
.rr-chip-active { border-color: var(--rr-ink); background: var(--rr-surface-2); color: var(--rr-ink); }
.rr-chip-clear { color: var(--rr-muted); }
.rr-chip-count { opacity: .65; }

.rr-filterbar { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; background: var(--rr-surface); border: 1px solid var(--rr-line); border-radius: 12px; padding: 12px 14px; margin-bottom: 14px; }
.rr-search { flex: 1 1 220px; background: var(--rr-surface-2); border: 1px solid var(--rr-line); border-radius: 8px; padding: 7px 10px; }
.rr-search input { border: none; background: transparent; outline: none; color: var(--rr-ink); font-family: inherit; font-size: 13.5px; width: 100%; }
.rr-root select { font-family: inherit; font-size: 13px; padding: 7px 10px; border-radius: 8px; border: 1px solid var(--rr-line); background: var(--rr-surface-2); color: var(--rr-ink); }
.rr-filter-count { font-size: 12.5px; color: var(--rr-muted); margin-left: auto; white-space: nowrap; }

.rr-table-wrap { background: var(--rr-surface); border: 1px solid var(--rr-line); border-radius: 14px; overflow: hidden; margin-bottom: 34px; }
.rr-table-scroll { overflow-x: auto; }
.rr-root table { width: 100%; border-collapse: collapse; min-width: 820px; }
.rr-root thead th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: var(--rr-muted); font-weight: 700; padding: 10px 14px; border-bottom: 1px solid var(--rr-line); background: var(--rr-surface-2); cursor: pointer; white-space: nowrap; }
.rr-root thead th.num { text-align: right; }
.rr-root tbody tr { border-bottom: 1px solid var(--rr-line); }
.rr-root tbody tr:last-child { border-bottom: none; }
.rr-root tbody tr:hover { background: var(--rr-surface-2); }
.rr-root tbody td { padding: 10px 14px; font-size: 13px; vertical-align: top; }
.rr-root td.num { text-align: right; font-variant-numeric: tabular-nums; }
.rr-co-name { font-weight: 700; line-height: 1.3; }
.rr-co-sub { font-size: 12px; color: var(--rr-muted); margin-top: 2px; }
.rr-status-chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px 4px 8px; border-radius: 999px; font-size: 12px; font-weight: 700; white-space: nowrap; }
.rr-trend { display: flex; gap: 3px; align-items: center; }
.rr-trend-dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }
.rr-badge { font-size: 10.5px; font-weight: 700; color: #fff; background: var(--rr-accent); border-radius: 5px; padding: 2px 6px; margin-left: 6px; letter-spacing: .03em; }
.rr-empty-row td { text-align: center; padding: 38px 14px; color: var(--rr-muted); font-size: 13px; }
.rr-mono { font-variant-numeric: tabular-nums; }
.rr-muted { color: var(--rr-muted); }
.rr-colour-change { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; white-space: nowrap; }
.rr-arrow { color: var(--rr-muted); font-size: 12px; }

.rr-footer { padding-top: 18px; border-top: 1px solid var(--rr-line); font-size: 12px; color: var(--rr-muted); display: flex; justify-content: space-between; gap: 16px; flex-wrap: wrap; }

@media (max-width: 860px) {
  .rr-panels { grid-template-columns: 1fr; }
  .rr-top { flex-direction: column; }
  .rr-meta { text-align: left; }
}
`;

export default RussiaRegisterPage;
